// Client-side mirror of supabase/functions/calculate-tax/rothGrowth.ts.
// Kept in sync so the wizard can render annualized-return previews without
// importing Deno code. The engine on the server is the source of truth for
// rothFutureValue — these helpers are display-only.

export type RothGrowthModel = 'straight' | 'fia1' | 'fia2' | 'fia3';

export interface FIAYear {
  indexedYield: number;
  bonusCredit: number;
}

export interface FIAStrategy {
  label: string;
  sequence: FIAYear[]; // length 10
}

export const FIA_STRATEGIES: Record<Exclude<RothGrowthModel, 'straight'>, FIAStrategy> = {
  fia1: {
    label: 'FIA Strategy 1 — Delayed Index Pop',
    sequence: [
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0.3382, bonusCredit: 0 },
      { indexedYield: 0.06,   bonusCredit: 0 },
      { indexedYield: 0.06,   bonusCredit: 0 },
      { indexedYield: 0.06,   bonusCredit: 0 },
      { indexedYield: 0.06,   bonusCredit: 0 },
      { indexedYield: 0.06,   bonusCredit: 0 },
    ],
  },
  fia2: {
    label: 'FIA Strategy 2 — Immediate Bonus + Steady Yield',
    sequence: [
      { indexedYield: 0.0555, bonusCredit: 0.20 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
    ],
  },
  fia3: {
    // 33.82% pop in Year 5, 20% bonus in Year 6 — intentionally separate years.
    label: 'FIA Strategy 3 — Delayed Pop + Rollover Bonus',
    sequence: [
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0,      bonusCredit: 0 },
      { indexedYield: 0.3382, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0.20 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
      { indexedYield: 0.0555, bonusCredit: 0 },
    ],
  },
};

export function projectFIASequence(principal: number, sequence: FIAYear[]): number {
  let value = principal;
  for (const { indexedYield, bonusCredit } of sequence) {
    const creditedRate = Math.max(0, indexedYield) + bonusCredit;
    value = value * (1 + creditedRate);
  }
  return value;
}

/**
 * Year-by-year pre-tax growth series for a Roth (or hypothetical IRA) under the chosen model.
 *
 * Returns `years + 1` entries: index 0 = `principal`, index N = pre-tax value at end of year N.
 * For 'straight' / undefined: compounds at `straightRate` for `years` steps regardless of the
 * scenario's stored `rothGrowthYears` — the projection chart is its own 10-year horizon.
 * For FIA models: walks the fixed 10-year sequence; if `years < 10`, truncates; if `years > 10`,
 * holds flat past Year 10 (no extrapolation past the defined sequence).
 */
export function rothYearByYearSeries(
  principal: number,
  model: RothGrowthModel | undefined,
  straightRate: number,
  years: number,
): number[] {
  const out: number[] = [principal];
  if (model && model !== 'straight') {
    const seq = FIA_STRATEGIES[model].sequence;
    let value = principal;
    for (let t = 1; t <= years; t++) {
      const step = seq[t - 1];
      if (step) {
        const creditedRate = Math.max(0, step.indexedYield) + step.bonusCredit;
        value = value * (1 + creditedRate);
      }
      out.push(value);
    }
    return out;
  }
  for (let t = 1; t <= years; t++) {
    out.push(principal * Math.pow(1 + straightRate, t));
  }
  return out;
}

export function calcRothFutureValue(
  principal: number,
  model: RothGrowthModel | undefined,
  straightRate: number,
  straightYears: number,
): number {
  if (model && model !== 'straight') {
    const strat = FIA_STRATEGIES[model];
    if (strat) return projectFIASequence(principal, strat.sequence);
  }
  return principal * Math.pow(1 + straightRate, straightYears);
}

/** 10-year annualized return implied by an FIA sequence, e.g. 0.0600 = 6.00%. */
export function fiaAnnualizedReturn(model: Exclude<RothGrowthModel, 'straight'>): number {
  const seq = FIA_STRATEGIES[model].sequence;
  const fv = projectFIASequence(1, seq);
  return Math.pow(fv, 1 / seq.length) - 1;
}

export function fiaTermYears(model: Exclude<RothGrowthModel, 'straight'>): number {
  return FIA_STRATEGIES[model].sequence.length;
}
